import * as yup from 'yup';
import { LoadedUser } from '../../interfaces';
import { isUserAdminOrManager } from '../../utils/global/helper';

export enum RepeatFrequency {
  D = 'D',
  W = 'W',
}

const requiredEmployeesSchema = yup
  .array()
  .of(yup.string().typeError('Please select a valid employee'))
  .min(1, 'Please select at least one employee')
  .required('Please select at least one employee');

const optionalEmployeesSchema = yup
  .array()
  .of(yup.string())
  .nullable()
  .optional()
  .notRequired();

export const getTimeBlockForm = (user: LoadedUser | null | undefined) =>
  yup.object({
    resourceBlockId: yup.number().nullable().optional().notRequired(),
    employeeId: yup.string().nullable().optional().notRequired(),
    name: yup.string().required('Please provide a name'),
    repeats: yup
      .mixed<RepeatFrequency | ''>()
      .oneOf(
        [...Object.values(RepeatFrequency), ''],
        'Invalid repeat frequency',
      )
      .nullable()
      .optional(),
    interval: yup.number().when('repeats', {
      is: (repeats: RepeatFrequency | '') => Boolean(repeats),
      then: schema =>
        schema
          .min(1, 'Interval must be at least 1')
          .required('Please provide a repeat interval'),
      otherwise: schema => schema.nullable().optional().notRequired(),
    }),
    isAllDay: yup
      .boolean()
      .required('Please specify if this time block is all day'),
    dateRange: yup.array().when('repeats', {
      is: (repeats: RepeatFrequency | '') => !repeats,
      then: schema => schema.nullable().optional().notRequired(),
      otherwise: schema =>
        schema
          .of(
            yup
              .date()
              .typeError('Please provide a valid date range')
              .required('Please provide a valid date range'),
          )
          .min(2, 'Please provide a start and end date')
          .max(2, 'Please provide a start and end date')
          .required('Please provide a start and end date'),
    }),
    date: yup.date().when('repeats', {
      is: (repeats: RepeatFrequency | '') => repeats,
      then: schema => schema.nullable().optional().notRequired(),
      otherwise: schema => schema.required('Please provide a blocked day'),
    }),
    startTime: yup.date().when('isAllDay', {
      is: false,
      then: schema => schema.required('Please provide a start time'),
      otherwise: schema => schema.nullable().optional().notRequired(),
    }),
    endTime: yup.date().when('isAllDay', {
      is: false,
      then: schema => schema.required('Please provide an end time'),
      otherwise: schema => schema.nullable().optional().notRequired(),
    }),
    employeeIds:
      user?.business.type === 'STUDIO' && isUserAdminOrManager(user)
        ? requiredEmployeesSchema
        : optionalEmployeesSchema,
    days: yup.array().when('repeats', {
      is: RepeatFrequency.W,
      then: schema =>
        schema
          .test({
            test: val => {
              const length = val?.length ?? 0;
              return (
                (length ?? 0) > 0 &&
                length <= 7 &&
                val?.every(
                  day => typeof day === 'number' && day >= 0 && day <= 6,
                )
              );
            },
            message: 'Please select at least one valid day',
          })
          .required('Please select at least one day'),
      otherwise: schema => schema.nullable().optional().notRequired(),
    }),
  });

export type TimeBlockFormValues = yup.InferType<
  ReturnType<typeof getTimeBlockForm>
>;
